import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  GraduationCap, 
  CheckCircle2, 
  Phone,
  ArrowLeft,
  ArrowRight,
  Shield,
  ClipboardCheck,
  AlertTriangle,
  FileText,
  Users,
  Building2
} from "lucide-react";
import { LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  Shield,
  ClipboardCheck,
  AlertTriangle,
  FileText,
  Users,
  Building2
};

export default function EducationSafetyAudits() {
  const { t, i18n } = useTranslation('services-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const services = [
    {
      icon: "ClipboardCheck",
      title: isRTL ? "סקר מקיף של מתקני המוסד" : "Comprehensive Facility Survey",
      description: isRTL ? "בדיקת כל מתקני בית הספר, גני הילדים או המוסד החינוכי" : "Inspection of all school, kindergarten, or educational institution facilities"
    },
    {
      icon: "AlertTriangle",
      title: isRTL ? "זיהוי סיכונים פוטנציאליים" : "Potential Risk Identification",
      description: isRTL ? "איתור מפגעי בטיחות וסיכונים ייחודיים לסביבת חינוך" : "Detection of safety hazards and risks unique to educational environments"
    },
    {
      icon: "FileText",
      title: isRTL ? "דו\"ח ממצאים מפורט" : "Detailed Findings Report",
      description: isRTL ? "דו\"ח מקיף עם המלצות לתיקון ושיפור" : "Comprehensive report with recommendations for correction and improvement"
    },
    {
      icon: "Shield",
      title: isRTL ? "עמידה בתקנות משרד החינוך" : "Ministry of Education Compliance",
      description: isRTL ? "וידוא עמידה בכל הדרישות הרגולטוריות" : "Ensuring compliance with all regulatory requirements"
    },
    {
      icon: "Users",
      title: isRTL ? "הדרכת צוות חינוכי" : "Educational Staff Training",
      description: isRTL ? "הכשרת מורים וצוות לזיהוי ומניעת סיכונים" : "Training teachers and staff to identify and prevent risks"
    },
    {
      icon: "Building2",
      title: isRTL ? "ליווי שוטף" : "Ongoing Support",
      description: isRTL ? "מעקב וליווי לאורך שנת הלימודים" : "Monitoring and support throughout the school year"
    }
  ];

  const institutionTypes = [
    isRTL ? "גני ילדים" : "Kindergartens",
    isRTL ? "בתי ספר יסודיים" : "Elementary Schools",
    isRTL ? "חטיבות ביניים" : "Middle Schools",
    isRTL ? "בתי ספר תיכוניים" : "High Schools",
    isRTL ? "מכללות" : "Colleges",
    isRTL ? "מוסדות חינוך מיוחד" : "Special Education Institutions"
  ];

  return (
    <Layout>
      <Helmet>
        <title>{isRTL ? "מבדקי בטיחות במוסדות חינוך | אדם בטיחות" : "Safety Audits in Educational Institutions | Adam Safety"}</title>
        <meta name="description" content={isRTL ? "מבדקי בטיחות מקצועיים לבתי ספר, גני ילדים ומוסדות חינוך. עמידה בתקנות משרד החינוך." : "Professional safety audits for schools, kindergartens, and educational institutions. Ministry of Education compliance."} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <GraduationCap className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isRTL ? "בטיחות במוסדות חינוך" : "Educational Safety"}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {isRTL ? "מבדקי בטיחות במוסדות חינוך" : "Safety Audits in Educational Institutions"}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {isRTL 
                ? "הגנה על הנכס היקר מכל - הילדים שלנו. מבדקי בטיחות מקיפים לבתי ספר, גנים ומוסדות חינוך בהתאם לתקנות משרד החינוך."
                : "Protecting our most precious asset - our children. Comprehensive safety audits for schools, kindergartens, and educational institutions in accordance with Ministry of Education regulations."
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {isRTL ? "לתיאום מבדק" : "Schedule an Audit"}
                </Button>
              </Link>
              <Link to="/services/safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {isRTL ? "שירותי בטיחות נוספים" : "More Safety Services"}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isRTL ? "מה כולל מבדק הבטיחות?" : "What's Included in the Safety Audit?"}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isRTL 
                ? "מבדק מקיף המותאם לצרכים הייחודיים של מוסדות חינוך"
                : "A comprehensive audit tailored to the unique needs of educational institutions"
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Shield;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-service"
                >
                  <div className="icon-container mb-4">
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Institution Types */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isRTL ? "מוסדות שאנחנו משרתים" : "Institutions We Serve"}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {institutionTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 bg-card p-4 rounded-xl border border-border"
              >
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                <span className="text-foreground font-medium">{type}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {isRTL ? "מוכנים להבטיח את בטיחות התלמידים?" : "Ready to Ensure Student Safety?"}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {isRTL 
                ? "צרו קשר עוד היום לתיאום מבדק בטיחות מקיף למוסד שלכם"
                : "Contact us today to schedule a comprehensive safety audit for your institution"
              }
            </p>
            <Link to="/contact">
              <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                <Phone className="w-5 h-5" />
                {isRTL ? "צרו קשר" : "Contact Us"}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
